export const MAX_BULK_NETWORK_ACTION_SIZE = 50

export type BulkNetworkActionFailure = {
  id: string
  message: string
}

export type BulkNetworkActionResult = {
  succeededIds: string[]
  failures: BulkNetworkActionFailure[]
}

/**
 * Runs `action` once per network, one after another, and reports each outcome
 * instead of stopping at the first failure (KBE-964). The caller gives every
 * call its own transaction, so a network a guard refuses (a ref branch based on
 * it, a release lock) is an answer about that network alone and the rest of the
 * batch still lands.
 *
 * Sequential on purpose: a delete cascades across every content table and takes
 * heavy locks (gotcha 41), so running a batch concurrently would make the
 * deletes queue behind each other until some hit `lock_timeout`.
 */
export async function runForEachNetwork(
  networkIds: readonly string[],
  action: (networkId: string) => Promise<unknown>,
): Promise<BulkNetworkActionResult> {
  const succeededIds: string[] = []
  const failures: BulkNetworkActionFailure[] = []
  for (const networkId of new Set(networkIds)) {
    try {
      await action(networkId)
      succeededIds.push(networkId)
    } catch (error) {
      // Returned to the client rather than thrown, so log it here or an
      // unexpected failure leaves no server-side trace.
      console.error(`Bulk network action failed for ${networkId}`, error)
      failures.push({
        id: networkId,
        message: error instanceof Error ? error.message : String(error),
      })
    }
  }
  return { succeededIds, failures }
}
